/**
 * lib/cards/render-custom-layout.js — Print path for tenants with a
 * saved custom layout.
 *
 * Chunk 3 fix (Apr 24 2026, post-Stan-deploy): Premium-based layouts
 * now correctly paint the tenant's uploaded banner (if any) instead
 * of always falling back to mountains.
 *
 * Previous bug: the code looked for a layout element of type
 * dynamic_banner. buildPremiumLayout never emits one — Premium paints
 * the banner procedurally. So saved layouts based on Premium had no
 * banner element, the existence-check failed, mountain was always
 * drawn.
 *
 * Fix: don't rely on a layout element. Use tenant.card_bottom_image_url
 * directly — same source template-premium.js uses.
 */

import { jsPDF } from 'jspdf';
import {
  CR80,
  generateQR,
  verifyUrl,
  applyDisplayNameMode,
  loadImageAsDataUrl,
  deriveInitials,
  drawPhotoPlaceholder,
  resolveBrandColor,
  stripProtocol,
  contrastingTextColor,
} from './shared.js';
import { drawWaveBand } from './defaults/wave.js';
import { drawMountainRange } from './defaults/mountains.js';
import { renderLayoutTo } from './render-layout.js';
import { resolvePremiumFooterColor } from './build-runtime-layout.js';

async function cropImageForBanner(dataUrl, sourceW, sourceH, targetWmm, targetHmm) {
  const pxPerMm = 10;
  const outW = Math.round(targetWmm * pxPerMm);
  const outH = Math.round(targetHmm * pxPerMm);

  const targetRatio = targetWmm / targetHmm;
  const sourceRatio = sourceW / sourceH;

  let sx, sy, sw, sh;
  if (sourceRatio > targetRatio) {
    sh = sourceH; sw = sourceH * targetRatio; sy = 0; sx = (sourceW - sw) / 2;
  } else {
    sw = sourceW; sh = sourceW / targetRatio; sx = 0; sy = (sourceH - sh) / 2;
  }

  const img = await loadHtmlImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  return canvas.toDataURL('image/jpeg', 0.92);
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

export async function renderCustomLayoutCard({ staff, tenant, staffSlug, layout }) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [CR80.widthMm, CR80.heightMm],
  });

  const brandColor = resolveBrandColor(tenant.brand_color);
  const displayName = applyDisplayNameMode(
    staff.first_name, staff.last_name, tenant.display_name_mode || 'full',
  );
  const footerParts = [tenant.public_phone, stripProtocol(tenant.public_website)].filter(Boolean);
  const initials = deriveInitials(staff.first_name, staff.last_name);
  const basedOn = layout.basedOnTemplate || 'minimal';

  // ── 1. White background ──
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, CR80.widthMm, CR80.heightMm, 'F');

  // ── 2. Banner or mountain (Premium only) / Wave (Minimal only) ──
  //
  // Banner geometry matches template-premium.js:
  //   banner slot: y 41-50 (9mm tall)
  //   footer strip: y 50-54 (4mm tall, solid brand colour)
  //
  // Wave band (Minimal) covers y 49-54 with tinted sine waves and a
  // brand-colour footer at the bottom.
  const bannerTopY = 41;
  const bannerHeight = 9;
  const footerY = 50;

  if (basedOn === 'minimal') {
    drawWaveBand(pdf, brandColor, 49);
  } else if (basedOn === 'premium') {
    let bannerRendered = false;

    // Use the uploaded banner if the tenant has one — matches
    // template-premium.js behaviour. Don't check for a layout element
    // (buildPremiumLayout never emits one).
    if (tenant.card_bottom_image_url) {
      try {
        const { dataUrl, width: natW, height: natH } = await loadImageAsDataUrl(tenant.card_bottom_image_url);
        const cropped = await cropImageForBanner(dataUrl, natW, natH, CR80.widthMm, bannerHeight);

        pdf.addImage(cropped, 'JPEG', 0, bannerTopY, CR80.widthMm, bannerHeight, undefined, 'FAST');

        const bannerOpacity = typeof tenant.card_banner_opacity === 'number'
          ? Math.max(0, Math.min(100, tenant.card_banner_opacity)) / 100
          : 1.0;

        if (bannerOpacity < 1.0 && pdf.GState && pdf.setGState) {
          const whiteAlpha = 1.0 - bannerOpacity;
          pdf.setGState(new pdf.GState({ opacity: whiteAlpha }));
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, bannerTopY, CR80.widthMm, bannerHeight, 'F');
          pdf.setGState(new pdf.GState({ opacity: 1.0 }));
        }

        bannerRendered = true;
      } catch (e) {
        console.warn('[custom-layout] banner failed, falling back to mountains:', e?.message || e);
      }
    }

    if (!bannerRendered) {
      drawMountainRange(pdf, brandColor, {
        topY: bannerTopY - 3,
        showBirds: true,
      });
    }

    // Brand-colour footer strip sits on top of banner bottom edge
    pdf.setFillColor(brandColor.r, brandColor.g, brandColor.b);
    pdf.rect(0, footerY, CR80.widthMm, CR80.heightMm - footerY, 'F');
  }

  // ── 3. Resolve async content for element pass ──
  const contentMap = {};

  if (tenant.logo_url) {
    try {
      const { dataUrl, width, height } = await loadImageAsDataUrl(tenant.logo_url);
      contentMap.el_logo = { imageData: dataUrl, width, height, format: 'PNG' };
    } catch (e) { /* fallback handled */ }
  }
  if (staff.photo_url) {
    try {
      const { dataUrl } = await loadImageAsDataUrl(staff.photo_url);
      contentMap.el_staff_photo = { imageData: dataUrl, format: 'PNG' };
    } catch (e) { /* fallback handled */ }
  }
  const qrUrl = verifyUrl(tenant.slug, staffSlug);
  contentMap.el_qr = { qrDataUrl: await generateQR(qrUrl, 512) };
  contentMap.el_name = { text: displayName };
  if (staff.role) contentMap.el_role = { text: staff.role };
  if (footerParts.length > 0) contentMap.el_footer_text = { text: footerParts.join('  ·  ') };

  // Bespoke banner layout elements (if a power-user added a
  // dynamic_banner element via future editor tooling, resolve it)
  const bannerEl = layout.elements.find((e) => e.type === 'dynamic_banner');
  if (bannerEl && tenant.card_bottom_image_url) {
    try {
      const { dataUrl, width: natW, height: natH } = await loadImageAsDataUrl(tenant.card_bottom_image_url);
      const cropped = await cropImageForBanner(dataUrl, natW, natH, bannerEl.w, bannerEl.h);
      const img = await loadHtmlImage(cropped);
      contentMap[bannerEl.id] = {
        imageData: cropped,
        width: img.naturalWidth,
        height: img.naturalHeight,
        format: 'JPEG',
      };
    } catch (e) { /* skip */ }
  }

  // ── 4. Element loop ──
  await renderLayoutTo(pdf, layout, contentMap, {
    brandColor,
    tenant,
    staff,
    skipBackground: true,
  });

  // ── 5. Premium footer text (post-loop, on top of footer strip) ──
  if (basedOn === 'premium' && footerParts.length > 0) {
    const auto = contrastingTextColor(brandColor);
    const color = resolvePremiumFooterColor(tenant.card_footer_text_color || 'auto', auto);
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(color.r, color.g, color.b);
    pdf.text(footerParts.join('  ·  '), CR80.widthMm / 2, 52.8, { align: 'center' });
  }

  // ── 6. Fallbacks ──
  if (!contentMap.el_staff_photo) {
    const photoEl = layout.elements.find((e) => e.type === 'dynamic_photo');
    if (photoEl) {
      drawPhotoPlaceholder(
        pdf, photoEl.x, photoEl.y, photoEl.w, photoEl.h, initials,
        { fontSize: photoEl.w > 25 ? 22 : 18 },
      );
    }
  }
  if (!contentMap.el_logo) {
    const logoEl = layout.elements.find((e) => e.type === 'dynamic_logo');
    if (logoEl) {
      const companyText = (tenant.card_company_name || tenant.name || '').trim();
      const baselineY = logoEl.y + (logoEl.h * 0.7);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(14, 26, 21);
      pdf.text(companyText, logoEl.x, baselineY);
    }
  }

  return pdf;
}
